import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import * as yup from "yup";

import * as RHFAddressValidationRules from "@/utils/validation/schemas/address.RHF";
import * as RHFUserValidationRules from "@/utils/validation/schemas/user.RHF";

import { DefaultAddressValidationSchema } from "./partials/defaultAddress.schema";

export const CustomerDefaultAddressValidationSchema = yup
    .object({
        addressing: RHFAddressValidationRules.addressing,
        firstName: RHFUserValidationRules.firstName,
        lastName: RHFUserValidationRules.lastName
    })
    .concat(DefaultAddressValidationSchema);

export type CustomerDefaultAddressFormValues = yup.InferType<
    typeof CustomerDefaultAddressValidationSchema
>;

export const RequiredCustomerDeliveryAddressValidationSchema = yup.object({
    [AddressType.Delivery]: yup
        .object({
            addressing: RHFAddressValidationRules.addressing,
            firstName: RHFUserValidationRules.firstName,
            lastName: RHFUserValidationRules.lastName
        })
        .concat(DefaultAddressValidationSchema)
        .required()
});

export type RequiredCustomerDeliveryAddressFormValues = yup.InferType<
    typeof RequiredCustomerDeliveryAddressValidationSchema
>;

export const RequiredCustomerPaymentAddressValidationSchema = yup.object({
    [AddressType.Payment]: yup
        .object({
            firstName: RHFUserValidationRules.firstName,
            lastName: RHFUserValidationRules.lastName
        })
        .concat(DefaultAddressValidationSchema)
        .required()
});

export type RequiredCustomerPaymentAddressFormValues = yup.InferType<
    typeof RequiredCustomerPaymentAddressValidationSchema
>;

export const OptionalCustomerDeliveryAddressValidationSchema = yup.object({
    [AddressType.Delivery]: yup
        .object({
            addressing: RHFAddressValidationRules.addressing,
            firstName: RHFUserValidationRules.firstName,
            lastName: RHFUserValidationRules.lastName
        })
        .concat(DefaultAddressValidationSchema)
        .optional()
});

export type OptionalCustomerDeliveryAddressFormValues = yup.InferType<
    typeof OptionalCustomerDeliveryAddressValidationSchema
>;

export const OptionalCustomerPaymentAddressValidationSchema = yup.object({
    [AddressType.Payment]: yup
        .object({
            firstName: RHFUserValidationRules.firstName,
            lastName: RHFUserValidationRules.lastName
        })
        .concat(DefaultAddressValidationSchema)
        .optional()
});

export type OptionalCustomerPaymentAddressFormValues = yup.InferType<
    typeof OptionalCustomerPaymentAddressValidationSchema
>;

export type CustomerDeliveryAddressFormValues =
    | RequiredCustomerDeliveryAddressFormValues
    | OptionalCustomerDeliveryAddressFormValues;

export type CustomerPaymentAddressFormValues =
    | RequiredCustomerPaymentAddressFormValues
    | OptionalCustomerPaymentAddressFormValues;

type CustomerAddressFormValuesByType = {
    [AddressType.Default]: CustomerDefaultAddressFormValues;
    [AddressType.Delivery]: CustomerDeliveryAddressFormValues;
    [AddressType.Payment]: CustomerPaymentAddressFormValues;
};

export type CustomerAddressFormValues<
    TAddressType extends keyof CustomerAddressFormValuesByType
> = CustomerAddressFormValuesByType[TAddressType];

export const BusinessDefaultAddressValidationSchema =
    DefaultAddressValidationSchema;

export type BusinessDefaultAddressFormValues = yup.InferType<
    typeof BusinessDefaultAddressValidationSchema
>;

export const BusinessDeliveryAddressValidationSchema = yup.object({
    [AddressType.Delivery]: DefaultAddressValidationSchema
});

export type BusinessDeliveryAddressFormValues = yup.InferType<
    typeof BusinessDeliveryAddressValidationSchema
>;

export const BusinessPaymentAddressValidationSchema = yup.object({
    [AddressType.Payment]: DefaultAddressValidationSchema
});

export type BusinessPaymentAddressFormValues = yup.InferType<
    typeof BusinessPaymentAddressValidationSchema
>;

type BusinessAddressFormValuesByType = {
    [AddressType.Default]: BusinessDefaultAddressFormValues;
    [AddressType.Delivery]: BusinessDeliveryAddressFormValues;
    [AddressType.Payment]: BusinessPaymentAddressFormValues;
};

export type BusinessAddressFormValues<
    TAddressType extends keyof BusinessAddressFormValuesByType
> = BusinessAddressFormValuesByType[TAddressType];
